import { selectBasket, selectBasketCustomer, selectBasketCustomerValidation, syncBasket } from 'containers/OrderDetails/basketSlice'
import { selectUserId } from 'containers/Login/loginSlice'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { getMissingInputs } from '../Contract/fields'
import { useDispatch, useSelector } from 'react-redux'

/**
 * Dane klienta i wymagane inputy (`requiredInputs`) z koszyka dla ekranu wyboru danych do faktury.
 * Przy wejściu na ekran wysyłamy koszyk (POST /cart) — po logowaniu / rejestracji koszyk musi
 * najpierw poznać klienta (POST /cart/customer przed tym zwraca błąd). Odpowiedź koszyka niesie
 * customer + customerValidation (z requiredInputs), więc nie potrzeba osobnego zapytania.
 * `waitForSync` pozwala zapytaniom do /cart/customer poczekać na zakończenie tej synchronizacji.
 */
const useCartCustomer = (isLogin) => {
  const { cartId } = useSelector(selectBasket)
  const customer = useSelector(selectBasketCustomer)
  const customerValidation = useSelector(selectBasketCustomerValidation)
  const userId = useSelector(selectUserId)
  // koszyk zna już zalogowanego klienta (blok customer nie jest trzymany w storage, więc to dane z tej sesji)
  const isCartSynced = !!customer?.id && customer.id === userId
  const syncRef = useRef(Promise.resolve())
  const dispatch = useDispatch()

  useEffect(() => {
    if (!isLogin || !cartId || isCartSynced) return
    // syncBasket dzieli zapytanie z innymi wywołaniami w locie (np. Reservation), więc koszyk idzie raz
    syncRef.current = Promise.resolve(dispatch(syncBasket())).catch(() => {
      console.warn('Cart sync: failed')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, dispatch, isLogin])

  const waitForSync = useCallback(() => syncRef.current, [])

  const requiredInputs = customerValidation?.requiredInputs
  const privateMissing = useMemo(
    () => getMissingInputs(customer, requiredInputs?.private),
    [customer, requiredInputs?.private]
  )

  return {
    customer,
    customerValidation,
    requiredInputs,
    waitForSync,
    // brak requiredInputs (np. błąd zapytania) = nie blokujemy lokalnie, zostaje walidacja przy „Dalej”
    isPrivateIncomplete: !!customer && privateMissing.length > 0,
  }
}

export default useCartCustomer
